import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Button, Checkbox, Icon, IconNames, Input, Skeleton } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import {
  DropdownPanel,
  Footer,
  GroupHeaderRow,
  GroupHeaderInner,
  ProfileRow,
  ProfileRowInner,
  AvatarWrap,
  NetworkBadge,
  ProfileLabel,
  StatusBadge,
  EmptyText,
  TriggerButtonWrap,
  TriggerButton,
} from './index.styles';
import { PLATFORM_COLOR_ICON_MAP, PLATFORM_DISPLAY_NAME_MAP } from '../../helpers';

interface ProfilePickerDropdownProps {
  allProfiles: ReportProfile[];
  selectedProfiles: ReportProfile[];
  onSelect: (profiles: ReportProfile[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const NEEDS_ATTENTION: ProfileStatus[] = ['Disconnected', 'MissingPermission'];

export const ProfilePickerDropdown = ({
  allProfiles,
  selectedProfiles,
  onSelect,
  disabled = false,
  isLoading = false,
}: ProfilePickerDropdownProps) => {
  const { t } = useTranslation('reports');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [failedAvatarIds, setFailedAvatarIds] = useState<Record<string, true>>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const { x, y, strategy, refs } = useFloating({
    open: isOpen,
    onOpenChange: (nextOpen) => {
      setIsOpen(nextOpen);
      if (!nextOpen) setSearch(''); // Reset search inline during state change
    },
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const selectedIds = useMemo(() => new Set(selectedProfiles.map((p) => p.id)), [selectedProfiles]);

  const grouped = useMemo(() => {
    const query = search.toLowerCase();
    const result: Record<string, ReportProfile[]> = {};

    allProfiles.forEach((p) => {
      if (query) {
        const platformDisplayName = (PLATFORM_DISPLAY_NAME_MAP[p.platform] ?? p.platform).toLowerCase();
        const matchesName = p.name.toLowerCase().includes(query);
        const matchesPlatform = platformDisplayName.includes(query);

        if (!matchesName && !matchesPlatform) return;
      }

      if (!result[p.platform]) result[p.platform] = [];

      result[p.platform].push(p);
    });

    return result;
  }, [allProfiles, search]);

  const allVisibleProfiles = useMemo(() => Object.values(grouped).flat(), [grouped]);

  const allVisibleSelected = allVisibleProfiles.length > 0 && allVisibleProfiles.every((p) => selectedIds.has(p.id));

  const handleToggleProfile = useCallback(
    (profile: ReportProfile) => {
      if (disabled) return;

      if (selectedIds.has(profile.id)) {
        onSelect(selectedProfiles.filter((p) => p.id !== profile.id));
      } else {
        onSelect([...selectedProfiles, profile]);
      }
    },
    [disabled, selectedIds, selectedProfiles, onSelect]
  );

  const handleToggleGroup = useCallback(
    (platform: string) => {
      if (disabled) return;

      const groupProfiles = grouped[platform] ?? [];
      const allGroupSelected = groupProfiles.every((p) => selectedIds.has(p.id));

      if (allGroupSelected) {
        const groupIds = new Set(groupProfiles.map((p) => p.id));

        onSelect(selectedProfiles.filter((p) => !groupIds.has(p.id)));
      } else {
        const existingIds = new Set(selectedProfiles.map((p) => p.id));
        const toAdd = groupProfiles.filter((p) => !existingIds.has(p.id));

        onSelect([...selectedProfiles, ...toAdd]);
      }
    },
    [disabled, grouped, selectedIds, selectedProfiles, onSelect]
  );

  const handleSelectAll = useCallback(() => {
    if (disabled) return;

    if (allVisibleSelected) {
      const visibleIds = new Set(allVisibleProfiles.map((p) => p.id));

      onSelect(selectedProfiles.filter((p) => !visibleIds.has(p.id)));
    } else {
      const existingIds = new Set(selectedProfiles.map((p) => p.id));
      const toAdd = allVisibleProfiles.filter((p) => !existingIds.has(p.id));

      onSelect([...selectedProfiles, ...toAdd]);
    }
  }, [disabled, allVisibleSelected, allVisibleProfiles, selectedProfiles, onSelect]);

  const markAvatarFailed = (id: string) => {
    setFailedAvatarIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  // Safe trigger click coordinator that handles clean state rollbacks
  const handleToggleDropdown = () => {
    setIsOpen((prev) => {
      const nextState = !prev;

      if (!nextState) setSearch('');

      return nextState;
    });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsidePanel = !panelRef.current || !panelRef.current.contains(target);

      if (isOutsideContainer && isOutsidePanel) {
        setIsOpen(false);
        setSearch(''); // Safely clear search inside macro task event
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderProfileRow = (profile: ReportProfile) => {
    const networkIcon = (PLATFORM_COLOR_ICON_MAP[profile.platform] ?? 'user') as IconNames;
    const canShowImg = !!profile.avatar && !failedAvatarIds[profile.id];
    const initial = (profile.name?.trim()?.[0] ?? '?').toUpperCase();
    const isChecked = selectedIds.has(profile.id);
    const needsAttention = NEEDS_ATTENTION.includes(profile.status);

    return (
      <ProfileRow
        key={profile.id}
        as="button"
        type="button"
        data-testid={`profile-picker-profile-${profile.id}`}
        onClick={() => handleToggleProfile(profile)}
        $isSelected={isChecked}
      >
        <ProfileRowInner>
          <Checkbox
            id={`profile-picker-checkbox-${profile.id}`}
            size="md"
            initialState={isChecked ? 'checked' : 'unchecked'}
            tabIndex={-1}
          />
          <AvatarWrap>
            {canShowImg ? (
              <img
                src={profile.avatar}
                alt={profile.name}
                onError={() => markAvatarFailed(profile.id)}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <p>{initial}</p>
            )}
            <NetworkBadge>
              <Icon name={networkIcon} />
            </NetworkBadge>
          </AvatarWrap>
          <ProfileLabel title={profile.name}>{profile.name}</ProfileLabel>
          {needsAttention && (
            <StatusBadge data-testid={`profile-picker-status-${profile.id}`}>
              <Icon name="warning" />
              {
                t(
                  `new_reports_profile_status_${profile.status.toLowerCase().replace('missing', 'missing_').replace('permission', 'permission')}`
                ) as string
              }
            </StatusBadge>
          )}
        </ProfileRowInner>
      </ProfileRow>
    );
  };

  const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

  return (
    <TriggerButtonWrap
      ref={(node) => {
        containerRef.current = node;
        refs.setReference(node); // Attaches ref safely to native wrapper wrapper element avoiding child render interception
      }}
    >
      {isLoading ? (
        <Skeleton
          height="2rem"
          width="5.5rem"
        />
      ) : (
        <TriggerButton
          data-testid="profile-picker-trigger"
          appearance="default"
          disabled={disabled}
          icon="plus_circle"
          label={t('new_reports_accounts_profiles') as string}
          onClick={handleToggleDropdown}
          size={12}
          variation="outline"
        />
      )}

      {isOpen &&
        dropdownRoot &&
        createPortal(
          <DropdownPanel
            ref={(node) => {
              refs.setFloating(node);
              panelRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            data-testid="profile-picker-dropdown"
          >
            <Input
              autoComplete="off"
              autoFocus
              change={setSearch}
              icon="search"
              id="profile-picker-search"
              label={t('new_reports_accounts_search_profiles') as string}
              maxLength={80}
              size="sm"
              value={search}
            />

            <ul data-testid="profile-picker-list">
              {allVisibleProfiles.length === 0 ? (
                <li>
                  <EmptyText>{t('new_reports_accounts_no_profiles') as string}</EmptyText>
                </li>
              ) : (
                Object.entries(grouped).map(([platform, profiles]) => {
                  const platformIcon = (PLATFORM_COLOR_ICON_MAP[platform] ?? 'user') as IconNames;
                  const allGroupSelected = profiles.every((p) => selectedIds.has(p.id));
                  const someGroupSelected = profiles.some((p) => selectedIds.has(p.id));

                  let groupState: 'checked' | 'indeterminate' | 'unchecked';

                  if (allGroupSelected) {
                    groupState = 'checked';
                  } else if (someGroupSelected) {
                    groupState = 'indeterminate';
                  } else {
                    groupState = 'unchecked';
                  }

                  return (
                    <li key={platform}>
                      <GroupHeaderRow
                        data-testid={`profile-picker-group-${platform}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleToggleGroup(platform)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleToggleGroup(platform);
                          }
                        }}
                      >
                        <GroupHeaderInner>
                          <Checkbox
                            id={`profile-picker-group-checkbox-${platform}`}
                            size="md"
                            initialState={groupState}
                            tabIndex={-1}
                          />
                          <Icon name={platformIcon} />
                          <span>{PLATFORM_DISPLAY_NAME_MAP[platform] ?? platform}</span>
                        </GroupHeaderInner>
                      </GroupHeaderRow>
                      <ul>{profiles.map(renderProfileRow)}</ul>
                    </li>
                  );
                })
              )}
            </ul>

            <Footer>
              <Button
                data-testid="profile-picker-select-all"
                appearance="primary"
                label={(allVisibleSelected ? t('new_reports_accounts_unselect_all') : t('new_reports_accounts_select_all')) as string}
                onClick={handleSelectAll}
                size={12}
                disabled={allVisibleProfiles.length === 0}
              />
            </Footer>
          </DropdownPanel>,
          dropdownRoot
        )}
    </TriggerButtonWrap>
  );
};
